import { useState, useEffect, useRef } from "react";

import {
  DEFAULT_SHOW_SUCCESS_DURATION,
  DEFAULT_SHOW_ERROR_DURATION,
  TRANSITION_DURATION,
  MIN_LOADING_STATE_DURATION,
} from "ui/button/loading-button/loading-button.constants";
import {
  type LoadingButtonIconPhase,
  LoadingButtonState,
} from "ui/button/loading-button/loading-button.types";

export const useStateTransition = (
  state?: LoadingButtonState,
  successDuration = DEFAULT_SHOW_SUCCESS_DURATION,
  errorDuration = DEFAULT_SHOW_ERROR_DURATION,
) => {
  const [operationState, setOperationState] = useState<LoadingButtonState>(
    LoadingButtonState.Default,
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [iconPhase, setIconPhase] = useState<LoadingButtonIconPhase>("visible");
  const loadingStartTime = useRef<number | null>(null);
  const pendingStateChange = useRef<{
    state: LoadingButtonState;
    time: number;
  } | null>(null);

  useEffect(() => {
    if (state === LoadingButtonState.Loading) {
      if (!loadingStartTime.current) {
        loadingStartTime.current = Date.now();
      }
      setOperationState(LoadingButtonState.Loading);
      return;
    }

    if (shouldDelayStateChange(state)) {
      return handleDelayedStateChange(state);
    }

    applyStateChange(state);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const shouldDelayStateChange = (
    newState?: LoadingButtonState,
  ): newState is typeof LoadingButtonState.Success | typeof LoadingButtonState.Error => {
    return Boolean(
      loadingStartTime.current &&
        (newState === LoadingButtonState.Success || newState === LoadingButtonState.Error),
    );
  };

  const handleDelayedStateChange = (
    newState: typeof LoadingButtonState.Success | typeof LoadingButtonState.Error,
  ) => {
    const elapsedTime = Date.now() - loadingStartTime.current!;
    const remainingTime = Math.max(0, MIN_LOADING_STATE_DURATION - elapsedTime);
    if (remainingTime > 0) {
      pendingStateChange.current = {
        state: newState,
        time: Date.now() + remainingTime,
      };
      const timeoutId = setTimeout(() => {
        if (pendingStateChange.current?.state === newState) {
          applyStateChange(newState);
        }
      }, remainingTime);
      return () => clearTimeout(timeoutId);
    }
    applyStateChange(newState);
  };

  const applyStateChange = (newState?: LoadingButtonState) => {
    loadingStartTime.current = null;
    pendingStateChange.current = null;

    if (newState === LoadingButtonState.Success) {
      return handleSuccessState();
    }

    if (newState === LoadingButtonState.Error) {
      return handleErrorState();
    }

    setOperationState(newState ?? LoadingButtonState.Default);
    setIsTransitioning(false);
    setIconPhase("visible");
  };

  const handleSuccessState = () => {
    setOperationState(LoadingButtonState.Success);
    setIconPhase("hidden");

    const SUCCESS_ANIMATION_SEQUENCE: { delay: number; phase: LoadingButtonIconPhase }[] = [
      { delay: TRANSITION_DURATION / 2, phase: "rotating" },
      { delay: TRANSITION_DURATION, phase: "scaling" },
      { delay: TRANSITION_DURATION * 2, phase: "visible" },
    ];

    const animationTimeouts = SUCCESS_ANIMATION_SEQUENCE.map(({ delay, phase }) =>
      setTimeout(() => setIconPhase(phase), delay),
    );

    if (successDuration !== Infinity) {
      const returnTimeout = setTimeout(() => {
        setIsTransitioning(true);

        setTimeout(() => {
          setOperationState(LoadingButtonState.Default);
          setIsTransitioning(false);
          setIconPhase("visible");
        }, TRANSITION_DURATION);
      }, successDuration);

      return () => {
        [...animationTimeouts, returnTimeout].forEach(clearTimeout);
      };
    }

    return () => {
      animationTimeouts.forEach(clearTimeout);
    };
  };

  const handleErrorState = () => {
    setOperationState(LoadingButtonState.Error);
    setIconPhase("visible");

    if (errorDuration !== Infinity) {
      const returnTimeout = setTimeout(() => {
        setIsTransitioning(true);

        setTimeout(() => {
          setOperationState(LoadingButtonState.Default);
          setIsTransitioning(false);
          setIconPhase("visible");
        }, TRANSITION_DURATION);
      }, errorDuration);

      return () => clearTimeout(returnTimeout);
    }
  };

  return {
    operationState,
    isTransitioning,
    iconPhase,
  };
};
